<script setup lang="ts">
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import PosUiAnchoredPopover from '@/components/pos-v2/ui/PosUiAnchoredPopover.vue';
  import PosUiSheet from '@/components/pos-v2/ui/PosUiSheet.vue';
  import type { TSheetHeight } from '@/components/pos-v2/ui/pos-ui-sheet.utils';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import type {
    ICatalogPanel,
    TCatalogPanelKind,
  } from '@/composition/catalog/catalog-page-state';
  import V2CatalogPanelContent from './V2CatalogPanelContent.vue';

  /**
   * The page's single floating panel (plan §5.9): a popover anchored to the
   * control that opened it on desktop and tablet, a bottom sheet on phones
   * (or when the anchor left the screen). One instance serves every row,
   * category and the page's ⋯.
   */
  const { state, layout, data } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const WIDTHS: Record<TCatalogPanelKind, number> = {
    availability: 360,
    price: 320,
    rowMenu: 240,
    categoryMenu: 240,
    moreMenu: 240,
  };

  /** Menus list up to ten entries: the 40 % sheet would cut them. */
  const SHEET_HEIGHTS: Record<TCatalogPanelKind, TSheetHeight> = {
    availability: 'sm',
    price: 'sm',
    rowMenu: 'md',
    categoryMenu: 'md',
    moreMenu: 'md',
  };

  const panel = computed(() => state.panel);
  const phone = computed(() => layout.tier.value === 'phone');
  /** Phones, or a panel whose anchor left the screen. */
  const asSheet = computed(() => phone.value || !panel.value?.anchor);

  const forCategory = computed(() => {
    const current = panel.value;
    return (
      current?.kind === 'categoryMenu' ||
      (current?.kind === 'availability' && current.entity === 'category')
    );
  });

  const item = computed(() => {
    const current = panel.value;
    if (!current || current.targetId === null || forCategory.value) return null;
    return data.itemById.value[current.targetId] ?? null;
  });

  const category = computed(() => {
    const current = panel.value;
    if (!current || current.targetId === null || !forCategory.value)
      return null;
    return data.categoryById.value.get(current.targetId) ?? null;
  });

  // Its product or category is gone (deleted here or elsewhere, or never
  // loaded): close, also when it opens that way.
  watch(
    [item, category],
    () => {
      const current = panel.value;
      if (
        current &&
        current.targetId !== null &&
        !item.value &&
        !category.value
      )
        state.panel = null;
    },
    { immediate: true }
  );

  const title = computed(() => {
    const current = panel.value;
    if (!current) return '';
    const name = item.value?.name ?? category.value?.name ?? '';
    if (current.kind === 'price') return t('menuPage.price.title');
    if (current.kind === 'availability')
      return current.turnOn
        ? t('menuPage.availability.showTitle')
        : t('menuPage.availability.hideTitle', { name });
    if (current.kind === 'moreMenu') return t('menuPage.more.title');
    return t('menuPage.rowMenu.title', { name });
  });

  /** Opens towards the side the anchor sits on. */
  const align = computed<'start' | 'end'>(() => {
    const anchor = panel.value?.anchor;
    if (!anchor) return 'start';
    const box = anchor.getBoundingClientRect();
    return box.left + box.width / 2 > window.innerWidth / 2 ? 'end' : 'start';
  });

  /** «Cambiar precio» opens with its first price selected. */
  const initialFocus = computed(() =>
    panel.value?.kind === 'price' ? 'input' : undefined
  );

  /** Changes with every panel, so swapped content takes the focus too. */
  const focusKey = ref(0);
  const sheetHeight = ref<TSheetHeight>('sm');
  watch(panel, (next: ICatalogPanel | null) => {
    if (!next) return;
    focusKey.value += 1;
    sheetHeight.value = SHEET_HEIGHTS[next.kind];
  });

  /**
   * The subtree a tier change would unmount (AGENTS keyboard rule): the
   * popover exists on tablet and desktop, an anchored sheet on phones.
   */
  const sheetTiers = computed(() =>
    panel.value?.anchor ? 'phone' : undefined
  );

  function onVisibility(open: boolean) {
    if (!open) state.panel = null;
  }
</script>

<template>
  <div class="v2-catalog-row-panels">
    <!-- Both stay mounted while closed: each open is a change they react
         to (focus, positioning). -->
    <PosUiAnchoredPopover
      v-if="!phone"
      :value="!!panel && !asSheet"
      :anchor="panel ? panel.anchor : null"
      :title="title"
      :align="align"
      :width="panel ? WIDTHS[panel.kind] : 240"
      :initial-focus="initialFocus"
      :focus-key="focusKey"
      @input="onVisibility"
    >
      <V2CatalogPanelContent
        v-if="panel && !asSheet"
        data-tier-only="tablet desktop"
        :panel="panel"
        :item="item"
        :category="category"
        :sheet="false"
      />
    </PosUiAnchoredPopover>

    <PosUiSheet
      :value="!!panel && asSheet"
      :title="title"
      :height.sync="sheetHeight"
      :max-width="phone ? undefined : 480"
      :initial-focus="initialFocus"
      :focus-key="focusKey"
      @input="onVisibility"
    >
      <div
        v-if="panel && asSheet"
        class="panels-sheet-body"
        :data-tier-only="sheetTiers"
      >
        <V2CatalogPanelContent
          :panel="panel"
          :item="item"
          :category="category"
          :sheet="true"
        />
      </div>
    </PosUiSheet>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-row-panels {
    .panels-sheet-body {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-4 tw-pb-4;
    }
  }
</style>
